import React from 'react';
import { Box, Typography, Container, Grid, Card, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Briefcase,
  GraduationCap,
  UserCheck,
  RefreshCw,
  Search,
  ClipboardCheck,
  FileText,
  Rocket,
  MessageSquare,
  Send,
  Award,
  ArrowRight,
  Check,
  Sparkles,
  PhoneCall,
} from 'lucide-react';

const PRIMARY = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const REGISTER_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSdUXBmeB096Bx_JreweZ9rSUG7K7HmtLqRgWTvPjrv3-RybBQ/viewform?usp=dialog';

const AUDIENCE = [
  { label: 'Freshers', icon: GraduationCap },
  { label: 'Final-year students', icon: Briefcase },
  { label: 'Working professionals', icon: UserCheck },
  { label: 'Career switchers', icon: RefreshCw },
  { label: 'Internship seekers', icon: Search },
];

const PROCESS = [
  {
    step: 'Step 1',
    title: 'Career Counseling & Registration',
    desc: 'One-on-one guidance to understand your goals, strengths, and the right career path before you begin.',
    icon: UserCheck,
  },
  {
    step: 'Step 2',
    title: 'Resume Creation & Optimization',
    desc: 'Build an industry-ready, ATS-friendly resume that highlights your skills and gets noticed by recruiters.',
    icon: FileText,
  },
  {
    step: 'Step 3',
    title: 'Skill Development & Live Training',
    desc: 'Hands-on live training to close the gap between academic knowledge and real industry requirements.',
    icon: Rocket,
  },
  {
    step: 'Step 4',
    title: 'Interview Preparation & Mock Sessions',
    desc: 'Practice mock interviews, improve communication, and build the confidence to face real interviews.',
    icon: MessageSquare,
  },
  {
    step: 'Step 5',
    title: 'Job Applications & Interview Opportunities',
    desc: 'Get connected with hiring companies and apply with our support to relevant job opportunities.',
    icon: Send,
  },
  {
    step: 'Step 6',
    title: 'Placement & Ongoing Career Support',
    desc: 'Land the job and continue receiving career support, mentorship, and growth guidance beyond placement.',
    icon: Award,
  },
];

const JobReadyProgramPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 8, bgcolor: '#F8FAFC' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          color: '#fff',
          py: { xs: 6, md: 8 },
          px: 2,
          mb: 6,
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #1e3a8a 100%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              startIcon={<ChevronLeft size={18} />}
              onClick={() => navigate('/')}
              sx={{
                color: 'rgba(255,255,255,0.9)',
                textTransform: 'none',
                fontWeight: 600,
                mb: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Back to Home
            </Button>
            <Typography
              sx={{
                color: '#93c5fd',
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
                mb: 1,
              }}
            >
              Job Ready Program
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              From Learning to Landing the Job
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.8,
                maxWidth: 760,
              }}
            >
              Reviving India is a career-focused platform dedicated to preparing students for
              today's competitive job market. We bridge the gap between education and employment
              with complete career support solutions.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowRight size={18} />}
              onClick={() => navigate('/contact')}
              sx={{
                mt: 3.5,
                bgcolor: '#fff',
                color: PRIMARY,
                fontWeight: 700,
                textTransform: 'none',
                py: 1.25,
                px: 4,
                borderRadius: '12px',
                '&:hover': { bgcolor: '#f1f5f9', color: PRIMARY_DARK },
              }}
            >
              Get Career Support
            </Button>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ px: 2 }}>
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Card
            sx={{
              p: { xs: 3, md: 5 },
              mb: 6,
              borderRadius: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              '&:hover': { transform: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' },
            }}
          >
            <Typography
              component="h2"
              sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, fontWeight: 700, color: '#1e293b', mb: 2 }}
            >
              Why a Job Ready Program?
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1.05rem', lineHeight: 1.85 }}>
              We understand that many students complete courses but still struggle with interviews,
              confidence, communication, and real industry exposure. That's why we provide complete
              career support solutions that bridge the gap between education and employment — so you
              don't just learn, you get hired.
            </Typography>
          </Card>
        </motion.div>

        {/* Who is this for */}
        <Typography
          component="h2"
          sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, fontWeight: 700, color: '#1e293b', mb: 1, textAlign: 'center' }}
        >
          Who Is This For?
        </Typography>
        <Typography sx={{ color: '#64748b', textAlign: 'center', mb: 4, fontSize: '1rem' }}>
          Our services are designed for everyone aiming to enter or grow in the job market.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 8 }} justifyContent="center">
          {AUDIENCE.map((item, i) => {
            const Icon = item.icon;
            return (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={item.label}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      height: '100%',
                      borderRadius: '18px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 16px 32px rgba(37,99,235,0.12)',
                        borderColor: PRIMARY,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        mx: 'auto',
                        mb: 1.5,
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #EAF2FF 0%, #DBEAFE 100%)',
                        color: PRIMARY,
                      }}
                    >
                      <Icon size={26} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                      {item.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {/* Our Process */}
        <Typography
          component="h2"
          sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, fontWeight: 700, color: '#1e293b', mb: 1, textAlign: 'center' }}
        >
          Our Process
        </Typography>
        <Typography sx={{ color: '#64748b', textAlign: 'center', mb: 5, fontSize: '1rem' }}>
          A clear, step-by-step journey from counseling to placement and beyond.
        </Typography>

        <Box sx={{ position: 'relative' }}>
          {/* Vertical connector line (desktop) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: '#dbeafe',
              transform: 'translateX(-50%)',
            }}
          />
          <Grid container spacing={4}>
            {PROCESS.map((item, i) => {
              const Icon = item.icon;
              const isLeft = i % 2 === 0;
              return (
                <Grid size={{ xs: 12, md: 6 }} key={item.step}>
                  <motion.div
                    initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.05 }}
                    style={{ height: '100%' }}
                  >
                    <Card
                      sx={{
                        p: { xs: 3, md: 3.5 },
                        height: '100%',
                        borderRadius: '18px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 16px 32px rgba(37,99,235,0.12)',
                          borderColor: PRIMARY,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 54,
                            height: 54,
                            borderRadius: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                            color: '#fff',
                            boxShadow: '0 6px 16px rgba(37,99,235,0.3)',
                          }}
                        >
                          <Icon size={26} />
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              color: PRIMARY,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              textTransform: 'uppercase',
                              letterSpacing: 1,
                              mb: 0.5,
                            }}
                          >
                            {item.step}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 1, lineHeight: 1.3 }}
                          >
                            {item.title}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.7 }}>
                            {item.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              mt: 8,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '28px',
              px: { xs: 3, md: 7 },
              py: { xs: 5, md: 7 },
              color: '#fff',
              background:
                'radial-gradient(120% 120% at 0% 0%, #1e3a8a 0%, #0f172a 45%, #0b1120 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -120,
                right: -90,
                width: 320,
                height: 320,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${PRIMARY}55 0%, transparent 70%)`,
                filter: 'blur(8px)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -140,
                left: -100,
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'radial-gradient(circle, #38bdf833 0%, transparent 70%)',
              },
            }}
          >
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.75,
                    py: 0.6,
                    mb: 2.5,
                    borderRadius: '999px',
                    bgcolor: 'rgba(147,197,253,0.14)',
                    border: '1px solid rgba(147,197,253,0.3)',
                  }}
                >
                  <Sparkles size={15} color="#93c5fd" />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#bfdbfe', letterSpacing: 0.5 }}>
                    ADMISSIONS OPEN · LIMITED SEATS
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: '1.6rem', md: '2.1rem' }, fontWeight: 800, mb: 1.5, lineHeight: 1.2 }}>
                  Ready to become job ready?
                </Typography>
                <Typography sx={{ color: 'rgba(226,232,240,0.85)', mb: 3, fontSize: '1.02rem', lineHeight: 1.7, maxWidth: 520 }}>
                  Counseling, resume building, live training, mock interviews, and real placement
                  support — one structured program that takes you from learning to hired.
                </Typography>
                <Grid container spacing={1.25}>
                  {['1:1 Career Counseling', 'Live Skill Training', 'Mock Interviews', 'Placement Support'].map((f) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={f}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(59,130,246,0.25)',
                            border: '1px solid rgba(147,197,253,0.4)',
                          }}
                        >
                          <Check size={13} color="#bfdbfe" strokeWidth={3} />
                        </Box>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#e2e8f0' }}>
                          {f}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(6px)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '20px',
                    p: { xs: 3, md: 3.5 },
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      mx: 'auto',
                      mb: 1.75,
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                      boxShadow: '0 8px 20px rgba(37,99,235,0.45)',
                    }}
                  >
                    <ClipboardCheck size={26} color="#fff" />
                  </Box>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, mb: 0.5 }}>
                    Join the Job Ready Program
                  </Typography>
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: '0.88rem', mb: 2.5 }}>
                    Takes ~2 minutes to register
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowRight size={18} />}
                    component="a"
                    href={REGISTER_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: '#fff',
                      color: PRIMARY,
                      fontWeight: 700,
                      textTransform: 'none',
                      py: 1.25,
                      borderRadius: '12px',
                      fontSize: '1rem',
                      '&:hover': { bgcolor: '#f1f5f9', color: PRIMARY_DARK },
                    }}
                  >
                    Register Now
                  </Button>
                  <Button
                    fullWidth
                    startIcon={<PhoneCall size={16} />}
                    onClick={() => navigate('/contact')}
                    sx={{
                      mt: 1.25,
                      color: '#cbd5e1',
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 1,
                      borderRadius: '12px',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
                    }}
                  >
                    Prefer to talk first? Get guidance
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default JobReadyProgramPage;
